import {
  Heading,
  Link,
  Placeholder,
  PromoCard,
  Slider,
  useData,
} from '@/components';
import { TEMPLATE_BASE_PATH } from '@/constants';
import { useSpecialsContext } from '@/context';
import {
  PromoCarouselDocumentDataCarouselItemsItem,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  PromoCarouselSlice,
} from '@/types';
import {
  getActivesPreCartDiscounts,
  getPreCartTreezDiscounts,
  getSlicePadding,
  getSpecialURL,
} from '@/utils';
import classNames from 'classnames';
import { CSSProperties, FC } from 'react';
import styles from './specialsslider.module.scss';
interface IPromoCarousel {
  slice?: PromoCarouselSlice;
}

const PromoCarousel: FC<IPromoCarousel> = ({ slice }) => {
  const { promoCarousel } = useData();
  const { isLoading, specials } = useSpecialsContext();

  // carousel props
  const activeAutoplay = !!promoCarousel?.autoplay;
  const delay = promoCarousel?.autoplay_delay ?? 2500;
  const waitForTransition = promoCarousel?.wait_for_transition;
  const disableOnInteraction = !!promoCarousel?.disable_autoplay_on_interaction;
  const speed = promoCarousel?.autoplay_speed ?? 1000;

  // slice props
  const background = slice?.primary.background_color;
  const paddingSection = slice?.primary.padding_section;
  const marginBottom = slice?.primary.bottom_spacing;
  const titleColor = slice?.primary.title_color;
  const seeAllHref = slice?.primary?.see_all_link;
  const seeMoreText = slice?.primary?.see_all_text;
  const seeAllTextColor = slice?.primary?.see_all_text_color;
  const activeControlColor = slice?.primary?.active_control_color;
  const disableControlColor = slice?.primary?.disable_control_color;
  const backgroundImage = slice?.primary?.background_image;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const cmsDiscountsItems = promoCarousel?.carousel_items;
  const title = slice?.primary?.title as string;

  const breakpoints = {
    1024: {
      spaceBetween: 16,
    },
  };

  if (!slice?.primary.active) {
    return null;
  }

  const preCartTreezDiscounts = getPreCartTreezDiscounts(specials);

  const activesPreCartDiscounts = getActivesPreCartDiscounts(
    cmsDiscountsItems as PromoCarouselDocumentDataCarouselItemsItem[],
    preCartTreezDiscounts,
  );

  const activesCount = Object.keys(activesPreCartDiscounts)?.length;

  const activeArrowColor = activeControlColor ?? 'initial';
  const disableArrowColor = disableControlColor ?? 'initial';
  const controlSlideBorder = seeAllTextColor
    ? '1px solid transparent'
    : 'initial';

  if (!isLoading && !activesCount) {
    return null;
  }

  return (
    <section
      className={classNames(
        styles.promo__carousel,
        getSlicePadding(paddingSection),
        {
          spacing_bottom: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: background!,
        backgroundImage: backgroundImage?.url
          ? `url(${backgroundImage?.url})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Slider
        activatedHover={false}
        activeAutoplay={activeAutoplay}
        autoplay={{ delay, disableOnInteraction, waitForTransition }}
        breakpoints={breakpoints}
        centeredSlides={false}
        className={classNames(styles.specials, 'special__carousel')}
        freeMode
        isNotPaddingMobile
        loop={activeAutoplay && activesCount > 3}
        seeAllElement={
          <Link
            aria-label={`Go to ${seeMoreText ?? 'See all'}`}
            className={styles.specials__see_all}
            href={seeAllHref ?? TEMPLATE_BASE_PATH.PROMOTIONS}
            style={{ color: seeAllTextColor! }}
          >
            {seeMoreText ?? 'SEE ALL'}
            <span>({activesCount})</span>
          </Link>
        }
        sliderHeading={
          title || promoCarousel?.title ? (
            <Heading
              className={styles.header}
              level={2}
              style={{ color: titleColor } as CSSProperties}
            >
              {title ?? promoCarousel?.title}
            </Heading>
          ) : undefined
        }
        spaceBetween={12}
        speed={speed}
      >
        {isLoading
          ? Array.from({ length: 15 }).map((_, index) => (
              <div className={styles.special__placeholder} key={index}>
                <Placeholder className={styles.special__placeholder_image} />
                <Placeholder className={styles.special__placeholder_title} />
                <Placeholder className={styles.special__placeholder_code} />
              </div>
            ))
          : (
              Object.values(
                activesPreCartDiscounts,
              ) as PromoCarouselDocumentDataCarouselItemsItemWithID[]
            )
              .slice(0, 15)
              .map((special, index) => (
                <PromoCard
                  className={styles.special}
                  key={index}
                  special={special}
                  specialUrl={getSpecialURL(special)}
                />
              ))}
      </Slider>
      <style>
        {`.special__carousel {
            .slick-arrow {
              color: ${activeArrowColor};
              border: ${controlSlideBorder};

              &:hover {
                color: ${activeArrowColor}; 
              }

              &.slick-prev {
                border: ${controlSlideBorder};

                &::after {
                  border-right: ${controlSlideBorder};
                }
              }

              &.slick-disabled {
                color: ${disableArrowColor};
              }
            }
          }
        `}
      </style>
    </section>
  );
};

export default PromoCarousel;
